// Helpers puros del control de uniformes (etapa/numero/agrupacion). Sin red.

export const ETAPA_LABEL = {
  sin_talla: 'Sin talla',
  por_pedir: 'Por pedir',
  en_pedido: 'En pedido',
  pedido: 'Pedido al proveedor',
  recibido: 'Recibido',
  entregado: 'Entregado',
}

// Variantes de <Badge>, no clases sueltas: el tema decide el color.
export const ETAPA_BADGE = {
  sin_talla: 'secondary',
  por_pedir: 'outline',
  en_pedido: 'warning',
  pedido: 'warning',
  recibido: 'info',
  entregado: 'success',
}

export const PEDIDO_ESTADO_LABEL = {
  borrador: 'Borrador',
  listo: 'Listo para enviar',
  enviado: 'Enviado',
  parcial: 'Recibido parcial',
  completo: 'Recibido completo',
  cancelado: 'Cancelado',
}

export const PEDIDO_ESTADO_BADGE = {
  borrador: 'secondary',
  listo: 'outline',
  enviado: 'warning',
  parcial: 'info',
  completo: 'success',
  cancelado: 'destructive',
}

// Piezas de una linea que llegaron y todavia no se reparten.
export const piezasDisponibles = (linea = {}, entregadosDeLinea = 0) =>
  Math.max(0, (linea?.cantidad_recibida || 0) - (entregadosDeLinea || 0))

// ctx = { pedido, linea, entregadosDeLinea, personalizada }. Sin ctx se comporta
// como antes de las lineas: decide con pedido_id + recibido_at.
export const etapaUniforme = (item = {}, ctx = {}) => {
  if ((item.estatus || 'no_entregado') === 'entregado') return 'entregado'
  if (!item.talla) return 'sin_talla'

  const { pedido = null, linea = null, entregadosDeLinea = 0, personalizada = true } = ctx
  const cancelado = pedido?.estado === 'cancelado'
  if (!item.pedido_id || cancelado) return 'por_pedir'
  if (pedido && pedido.estado !== 'enviado') return 'en_pedido'

  // Personalizada: la pieza es de este jugador. Generica: basta con que quede
  // pieza libre de su talla, sin importar a nombre de quien se pidio.
  if (personalizada || !linea) return item.recibido_at ? 'recibido' : 'pedido'
  return piezasDisponibles(linea, entregadosDeLinea) > 0 ? 'recibido' : 'pedido'
}

// Las tres reglas del candado. El servidor repite esta misma logica en
// actualizarAsignacion: si cambia aqui, tiene que cambiar alla.
export const puedeEntregar = (item = {}, ctx = {}) => {
  if (!item.talla) return false
  const { pedido = null, linea = null, entregadosDeLinea = 0, personalizada = true } = ctx

  // Regla 3: nunca se pidio -> el sistema no opina, se entrega libre.
  if (!item.pedido_id && !item.linea_id) return true
  // Su pedido se cancelo -> el sistema ya no espera esa prenda.
  if (pedido?.estado === 'cancelado') return true
  // Cualquier otro caso con pedido_id puesto (linea_id en NULL a medias, o sin
  // contexto de pedido) falla cerrado: no se entrega sin comprobacion.
  if (!item.linea_id || !pedido) return false
  if (pedido.estado !== 'enviado') return false

  if (personalizada) return !!item.recibido_at            // regla 1
  return piezasDisponibles(linea, entregadosDeLinea) > 0  // regla 2
}

// Numero efectivo: numero_jersey del grupo del item; fallback al numero del club.
// Un valor congelado en la prenda es lo que quedo bordado: gana sobre el padron.
// Vacio o null = todavia no se congela, asi que sigue al padron como siempre.
const congelado = (v) => (v != null && v !== '' ? v : null)

export const numeroEfectivo = (item = {}, jugador = {}) => {
  const impreso = congelado(item.numero_impreso)
  if (impreso != null) return impreso
  const gid = item.grupo_id || null
  if (gid) {
    const jg = (jugador.jugadores_grupos || []).find((g) => g.grupo_id === gid)
    if (jg && jg.numero_jersey != null) return jg.numero_jersey
  }
  return jugador.numero != null ? jugador.numero : null
}

// Gemelo de numeroEfectivo: el del grupo del item, con el del club de respaldo.
export const nicknameEfectivo = (item = {}, jugador = {}) => {
  const impreso = congelado(item.nickname_impreso)
  if (impreso != null) return impreso
  const gid = item.grupo_id || null
  if (gid) {
    const jg = (jugador.jugadores_grupos || []).find((g) => g.grupo_id === gid)
    if (jg && jg.nickname) return jg.nickname
  }
  return jugador.nickname || null
}

// Parcial y completo NO se guardan: se cuentan desde las lineas.
export const estadoPedido = (pedido = {}, lineas = []) => {
  const estado = pedido?.estado || 'borrador'
  if (estado !== 'enviado') return estado
  const pedidas = (lineas || []).reduce((s, l) => s + (l.cantidad_pedida || 0), 0)
  const recibidas = (lineas || []).reduce((s, l) => s + (l.cantidad_recibida || 0), 0)
  if (pedidas > 0 && recibidas >= pedidas) return 'completo'
  if (recibidas > 0) return 'parcial'
  return 'enviado'
}

// Unico constructor del ctx: lo usan Lista, Estacion, Tablero y agruparPorGrupo.
// Si la matriz cambia de forma, se cambia aqui y en ningun otro lado.
export const ctxDeEntrega = (entrega = {}) => ({
  pedido: entrega.pedido_estado ? { estado: entrega.pedido_estado } : null,
  linea: entrega.linea || null,
  entregadosDeLinea: entrega.entregados_de_linea || 0,
  personalizada: entrega.personalizada !== false,
})

// Lineas "por pedir" agrupadas por prenda+talla (solo items con registro real).
export const agruparNecesidades = (entregas = []) => {
  const porPedir = (entregas || []).filter(
    (e) => e.asignacionId && etapaUniforme(e, ctxDeEntrega(e)) === 'por_pedir',
  )
  const map = new Map()
  porPedir.forEach((e) => {
    const key = `${e.prenda_id}__${e.talla}`
    if (!map.has(key)) map.set(key, { prenda_id: e.prenda_id, talla: e.talla, cantidad: 0, items: [] })
    const g = map.get(key)
    g.cantidad += 1
    g.items.push(e)
  })
  return [...map.values()]
}

// Subtotales por grupo (grupo_id del item; null -> 'sin_grupo').
export const agruparPorGrupo = (entregas = []) => {
  const map = new Map()
  ;(entregas || []).forEach((e) => {
    const gid = e.grupo_id || 'sin_grupo'
    if (!map.has(gid)) {
      map.set(gid, { grupoId: gid, total: 0, porPedir: 0, enPedido: 0, pedido: 0, recibido: 0, entregado: 0, sinCobrar: 0 })
    }
    const g = map.get(gid)
    g.total += 1
    // Con contexto: sin el, "en pedido" seria inalcanzable y los conteos mentirian.
    const etapa = etapaUniforme(e, ctxDeEntrega(e))
    if (etapa === 'por_pedir') g.porPedir += 1
    else if (etapa === 'en_pedido') g.enPedido += 1
    else if (etapa === 'pedido') g.pedido += 1
    else if (etapa === 'recibido') g.recibido += 1
    else if (etapa === 'entregado') g.entregado += 1
    if (!e.pagado) g.sinCobrar += 1
  })
  return [...map.values()]
}
